import { type ComponentType, type LazyExoticComponent, lazy } from 'react';

import { isChunkLoadError, reloadOnce } from './staleChunk';

// eslint-disable-next-line @typescript-eslint/no-explicit-any -- the bound React itself puts on `lazy`.
type Page = ComponentType<any>;

/**
 * A page loaded when its route is first visited (`AM-197`): each page is its own chunk, so the
 * console no longer downloads every screen before showing the first one. A page still loading
 * suspends inside the layout's own boundary (`PageOutlet`), so only the content waits for it: the
 * router puts no boundary around a route without a pending component, and the nearest one above
 * the layout would hide the whole screen, menu included.
 *
 * A chunk that is gone (a tab opened before a deploy, `staleChunk.ts`) reloads the page once
 * instead of failing: `React.lazy` keeps a failed import for good, so only a reload recovers. Until
 * the reload happens the page stays suspended, behind the content's loader. When the guard refuses
 * the reload (one just happened, or there is no connection) the error goes on to the route's
 * boundary, which offers the reload itself (`RouteErrorPage`).
 */
export function lazyPage<M extends Record<K, Page>, K extends keyof M & string>(
  load: () => Promise<M>,
  name: K,
): LazyExoticComponent<M[K]> {
  return lazy(async () => {
    try {
      return { default: (await load())[name] };
    } catch (error) {
      if (isChunkLoadError(error) && reloadOnce()) {
        return new Promise<never>(() => {});
      }
      throw error;
    }
  });
}
